// Curriculum mechanics catalog — the hand-maintained source of truth that maps
// every PLAYABLE game mechanic to (a) one of the five money frameworks and (b) one
// of the four learning brackets. This is documentation that travels with the code:
// when a mechanic ships, add it here so the Curriculum Alignment export stays
// complete and accurate.
//
// Framework choice = the single principle the mechanic most directly teaches (a
// mechanic can touch several; we tag its headline lesson). Bracket = the earliest
// stage the mechanic is developmentally appropriate for; where the build enforces a
// hard age gate, `unlockAge` records it and the bracket is derived from it.

import type { Framework } from '../routes/builder/weeklyDecisionDeck.js';

/** The four learning brackets, aligned to engine `visionBracket()` boundaries
 *  (8–10 / 11–13 / 14–16 / 17–18). Numeric id 1..4. */
export type Bracket = 1 | 2 | 3 | 4;

export const BRACKETS: Record<Bracket, { id: Bracket; label: string; ages: string }> = {
  1: { id: 1, label: 'Foundations', ages: '8–10' },
  2: { id: 2, label: 'Explorer', ages: '11–13' },
  3: { id: 3, label: 'Builder', ages: '14–16' },
  4: { id: 4, label: 'Founder', ages: '17–18' },
};

export type Mechanic = {
  id: string;
  name: string;
  framework: Framework;
  bracket: Bracket;
  unlockAge?: number; // present when the build enforces an in-game age gate
  description: string; // what the player DOES, and the lesson it earns through play
};

// Ordered by bracket, then framework, for a readable report.
export const MECHANICS: Mechanic[] = [
  // ── Foundations (8–10) ──────────────────────────────────────────────────────
  {
    id: 'morning-ritual',
    name: 'Morning ritual & mindset edge',
    framework: 'Compounding',
    bracket: 1,
    description:
      'A daily gratitude/intention ritual that builds a streak; the disciplined "growth" choice grants a one-week demand edge. Tiny habits done daily compound into an advantage.',
  },
  {
    id: 'reflect-journal',
    name: 'Reflect & purpose journaling',
    framework: 'Reputation',
    bracket: 1,
    description:
      'A short end-of-day reflection that feeds an Ikigai/purpose profile. Connects money to meaning and self-knowledge — the foundation of a good name and good choices.',
  },
  {
    id: 'give',
    name: 'Generosity (give to a cause)',
    framework: 'Reputation',
    bracket: 1,
    description:
      'Spend cash to give to a cause. Costs money now and earns nothing directly — teaching that generosity is a habit that builds standing and community.',
  },

  // ── Explorer (11–13) ────────────────────────────────────────────────────────
  {
    id: 'pricing-supply-demand',
    name: 'Pricing Studio (supply & demand)',
    framework: 'Cashflow',
    bracket: 2,
    description:
      'Drag the price and watch a live demand curve: too dear and customers vanish, too cheap and you leave money on the table. A computed "sweet spot" marks the profit-max price.',
  },
  {
    id: 'cashflow-statement',
    name: 'Cashflow statement (profit & overheads)',
    framework: 'Cashflow',
    bracket: 2,
    description:
      'A real weekly statement from the ledger: revenue − variable costs − fixed overheads = profit. Teaches the difference between variable and fixed costs and how overheads work.',
  },
  {
    id: 'energy-balance',
    name: 'Energy & work–fun balance',
    framework: 'Risk',
    bracket: 2,
    description:
      'Energy scales business demand; grinding (Push Hard) drains it and burnout cuts sales, while a Break restores it for a cost. Teaches sustainability and the risk of overwork.',
  },
  {
    id: 'savings-interest',
    name: 'Savings jar with interest',
    framework: 'Compounding',
    bracket: 2,
    description:
      'Deposit cash into savings that earns interest every week. The first taste of money making money while you sleep.',
  },
  {
    id: 'assets-liabilities',
    name: 'Assets vs liabilities classifier',
    framework: 'Cashflow',
    bracket: 2,
    description:
      'Sort items into things that put money IN your pocket (assets) vs take it OUT (liabilities). The Rich Dad core distinction, made interactive.',
  },
  {
    id: 'freedom-meter',
    name: 'Freedom Meter (passive ≥ expenses)',
    framework: 'Cashflow',
    bracket: 2,
    description:
      'A live meter of passive income vs living costs; hitting 100% unlocks Freedom Mode. Defines the win condition of the whole game in one number.',
  },
  {
    id: 'buy-passive-asset',
    name: 'Buy an income asset (Course Creator)',
    framework: 'Compounding',
    bracket: 2,
    description:
      'Raise and spend capital on an asset that pays passive weekly income forever. The first asset that moves the Freedom Meter on its own.',
  },
  {
    id: 'profit-harvest',
    name: 'Payday harvest (keep / save / invest)',
    framework: 'Compounding',
    bracket: 2,
    description:
      'After each profitable week, choose to keep cash, save it at interest, or buy an asset. The moment a weekly profit becomes a long-term decision.',
  },
  {
    id: 'loans',
    name: 'Loans & repayment (debt)',
    framework: 'Leverage',
    bracket: 2,
    description:
      'Borrow a lump sum and repay it weekly with interest. Teaches that borrowed money is a tool with a cost — good when it buys something that earns more than it costs.',
  },
  {
    id: 'investors',
    name: 'Raise money from investors',
    framework: 'Leverage',
    bracket: 2,
    description:
      'Take cash now in exchange for a share of weekly revenue (never repaid). Contrasts equity with debt — leverage other people’s money to grow faster.',
  },
  {
    id: 'vision-board',
    name: 'Vision Board (goals)',
    framework: 'Compounding',
    bracket: 2,
    description:
      'Pin up to six dreams; they turn from grey to colour as wealth/freedom targets are met. Teaches long-term goal-setting and that goals are reached by doing the work.',
  },
  {
    id: 'mastermind',
    name: 'Mastermind circle (mentors)',
    framework: 'Reputation',
    bracket: 2,
    unlockAge: 11,
    description:
      'Recruit up to four mentors from a roster of eight; each speaks in character when your situation calls for it. Teaches that who you learn from shapes how you grow.',
  },
  {
    id: 'weekly-decisions',
    name: 'Weekly decision cards (delayed consequences)',
    framework: 'Risk',
    bracket: 2,
    description:
      'A dilemma each week whose consequence lands weeks later, then names its framework and lesson. Teaches that today’s choices pay off — or cost — down the road.',
  },
  {
    id: 'resilience',
    name: 'Failure & resilience',
    framework: 'Risk',
    bracket: 2,
    description:
      'A loss week is reframed as a lesson (never "game over"): a What-Happened breakdown, mentor reactions, and a recovery plan; bouncing back awards +Persistence.',
  },
  {
    id: 'venture-services',
    name: 'Venture: Services (sell your time)',
    framework: 'Cashflow',
    bracket: 2,
    unlockAge: 12,
    description:
      'Set an hourly rate and choose hours (capped by demand AND energy). High margin but hard-capped by hours — the S-quadrant ceiling: selling yourself doesn’t scale.',
  },
  {
    id: 'venture-social',
    name: 'Venture: Social Media (audience as asset)',
    framework: 'Compounding',
    bracket: 2,
    unlockAge: 13,
    description:
      'Post content (costs energy, no cash) to grow followers; milestones unlock brand deals that pay passive income. An audience built once pays you for years.',
  },
  {
    id: 'venture-trade',
    name: 'Venture: Trade & Resell (buy low, sell high)',
    framework: 'Risk',
    bracket: 2,
    unlockAge: 13,
    description:
      'Buy undervalued items from a weekly market, hold (paying a carry cost), sell at fair value. Predicted value is a range, not a promise — research, patience, and timing, the Buffett way.',
  },

  // ── Builder (14–16) ─────────────────────────────────────────────────────────
  {
    id: 'venture-gaming',
    name: 'Venture: Gaming (skill is the investment)',
    framework: 'Risk',
    bracket: 3,
    unlockAge: 14,
    description:
      'Practise to raise skill (diminishing returns) or compete for a skill-scaled prize; go pro for passive streaming income. Skill — not luck — is what pays; explicitly not gambling.',
  },
  {
    id: 'year-progression',
    name: 'Age & year progression',
    framework: 'Compounding',
    bracket: 3,
    description:
      'Advance an in-game year at a time with an Annual Review; income and assets carry forward. Teaches the long view — wealth is built over years, not weeks.',
  },

  // ── Founder (17–18) ─────────────────────────────────────────────────────────
  {
    id: 'cashflow-quadrant',
    name: 'Cashflow Quadrant (E/S/B/I)',
    framework: 'Leverage',
    bracket: 4,
    description:
      'Your income mix infers your quadrant (Employee/Self-employed/Business/Investor); the game nudges you from trading time toward owning systems and investments.',
  },
  {
    id: 'inflation-snowball',
    name: 'Inflation & the Buffett snowball',
    framework: 'Compounding',
    bracket: 4,
    description:
      'Cash left idle for years triggers an inflation warning — idle money shrinks, invested money compounds. The snowball that turns small, patient sums into mountains.',
  },
  {
    id: 'legacy',
    name: 'Legacy & impact (age 18 finale)',
    framework: 'Reputation',
    bracket: 4,
    description:
      'A closing summary of the life built — wealth, freedom, and who was lifted along the way. Frames the end goal as significance, not just accumulation.',
  },
];

/** Display version shown in the parent dashboard footer (the long-press target). */
export const APP_VERSION = '0.9.0';
